/**
 * Rivo favorites: UI follows customer-scoped localStorage on load and on click.
 * RivoAPI.favorite_products() reconciles only when it disagrees with localStorage (server wins),
 * except briefly after a click when the list API can be stale.
 */
(function () {
  const SELECTOR = "[data-bww-rivo-wishlist]";
  const STORAGE_KEY_PREFIX = "bww:rivo:favorites:";

  /** How long to skip applying server list after a click (favorite_products() may lag). */
  const RECONCILE_PAUSE_MS = 4000;

  /** Background attempts — RivoAPI often loads late. */
  const SYNC_DELAYS_MS = [0, 500, 2000, 5000, 12000, 30000, 60000];

  /** After click add/remove; while active, mismatched sync results are not applied. */
  let reconcilePausedUntil = 0;

  function shopifyCustomerLoggedIn() {
    return document.body?.dataset?.customerLoggedIn === "true";
  }

  function customerIdForStorage() {
    const fromBody = document.body?.dataset?.customerId;
    if (fromBody) return String(fromBody);
    if (typeof window !== "undefined" && window.customer && window.customer.id) {
      return String(window.customer.id);
    }
    return null;
  }

  function storageKey() {
    if (!shopifyCustomerLoggedIn()) return null;
    const id = customerIdForStorage();
    return id ? STORAGE_KEY_PREFIX + id : null;
  }

  /**
   * @returns {Set<string>}
   */
  function readFavoriteIdsFromStorage() {
    const key = storageKey();
    if (!key) return new Set();
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return new Set();
      const out = new Set();
      for (let i = 0; i < parsed.length; i++) {
        const n = normalizeProductId(parsed[i]);
        if (n) out.add(n);
      }
      return out;
    } catch {
      return new Set();
    }
  }

  /**
   * @param {Set<string>} ids
   */
  function writeFavoriteIdsToStorage(ids) {
    const key = storageKey();
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify([...ids].sort()));
    } catch {
      /* quota / private mode */
    }
  }

  /**
   * @param {unknown} value
   * @returns {string | null}
   */
  function normalizeProductId(value) {
    if (value == null || value === "") return null;
    const s = String(value).trim();
    const gid = s.match(/Product\/(\d+)/i);
    if (gid) return gid[1];
    if (/^\d+$/.test(s)) return s;
    return s;
  }

  /**
   * @param {unknown} data
   * @returns {object[]}
   */
  function extractFavoriteProducts(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.products)) return data.products;
    if (data.data && Array.isArray(data.data.products)) return data.data.products;
    if (Array.isArray(data.favorite_products)) return data.favorite_products;
    return [];
  }

  /**
   * @param {object[]} products
   * @returns {Set<string>}
   */
  function favoriteIdSetFromProducts(products) {
    const ids = new Set();
    for (let i = 0; i < products.length; i++) {
      const p = products[i];
      if (!p || typeof p !== "object") continue;
      const candidates = [p.remote_id, p.shopify_product_id, p.product_id, p.shopify_id, p.id];
      for (let j = 0; j < candidates.length; j++) {
        const n = normalizeProductId(candidates[j]);
        if (n) ids.add(n);
      }
      const variants = p.variants;
      if (Array.isArray(variants)) {
        for (let k = 0; k < variants.length; k++) {
          const v = variants[k];
          if (v && v.product_id != null) {
            const n = normalizeProductId(v.product_id);
            if (n) ids.add(n);
          }
        }
      }
    }
    return ids;
  }

  /**
   * @param {Set<string>} a
   * @param {Set<string>} b
   * @returns {boolean}
   */
  function setsEqual(a, b) {
    if (a.size !== b.size) return false;
    for (const x of a) {
      if (!b.has(x)) return false;
    }
    return true;
  }

  /**
   * @param {Set<string>} ids
   */
  function applyFavoriteStateToButtons(ids) {
    document.querySelectorAll(SELECTOR + "[data-product-id]").forEach(function (btn) {
      const raw = btn.getAttribute("data-product-id");
      const id = normalizeProductId(raw);
      if (!id) return;
      const on = ids.has(id);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
      btn.classList.toggle("rivo-wishlist--active", on);
      const addL = btn.getAttribute("data-label-add");
      const remL = btn.getAttribute("data-label-remove");
      if (addL && remL) {
        btn.setAttribute("aria-label", on ? remL : addL);
      }
    });
  }

  /** 1) Page load: paint from localStorage only. */
  function hydrateUIFromStorage() {
    if (!shopifyCustomerLoggedIn()) {
      applyFavoriteStateToButtons(new Set());
      return;
    }
    applyFavoriteStateToButtons(readFavoriteIdsFromStorage());
  }

  /**
   * 2) Rivo loaded: if server list matches localStorage, no-op; else update storage + buttons.
   * After a recent click, skip applying a mismatch (stale list API).
   * `favorite_products` per Rivo SDK: `favorite_products("loggedin", currencyCode)` when available.
   */
  function favoriteProductsPromise() {
    const api = window.RivoAPI;
    if (!api || typeof api.favorite_products !== "function") return Promise.resolve(null);
    const cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const first =
      cur != null && String(cur).length > 0
        ? Promise.resolve(api.favorite_products("loggedin", cur))
        : Promise.resolve(api.favorite_products());
    return first.catch(function () {
      return api.favorite_products();
    });
  }

  function syncFromRivo() {
    favoriteProductsPromise()
      .then(function (data) {
        if (data == null || !shopifyCustomerLoggedIn()) return;

        const serverIds = favoriteIdSetFromProducts(extractFavoriteProducts(data));
        const localIds = readFavoriteIdsFromStorage();

        if (setsEqual(serverIds, localIds)) return;

        if (Date.now() < reconcilePausedUntil) return;

        writeFavoriteIdsToStorage(serverIds);
        applyFavoriteStateToButtons(serverIds);
      })
      .catch(function () {});
  }

  /**
   * @param {unknown} resp
   */
  function applyAddRemoveResponseToRivoStore(resp) {
    if (!resp || typeof resp !== "object") return;
    const products = extractFavoriteProducts(resp);
    if (products.length === 0) return;
    applyProductsToRivoFavoriteStore(products);
  }

  function scheduleBackgroundSync() {
    for (let i = 0; i < SYNC_DELAYS_MS.length; i++) {
      setTimeout(syncFromRivo, SYNC_DELAYS_MS[i]);
    }

    window.addEventListener("load", function () {
      syncFromRivo();
      setTimeout(syncFromRivo, 1500);
      setTimeout(syncFromRivo, 8000);
    });
  }

  /**
   * Open the Rivo Account Widget **home** (`#rivo`), not a deep tab (e.g. favorites).
   * Clicks the header account link when present so the drawer opens reliably.
   */
  function openRivoAccountHome() {
    window.location.hash = "#rivo";
    const trigger = document.querySelector('a[href="#rivo"]');
    if (trigger && typeof trigger.click === "function") {
      trigger.click();
    }
  }

  function openRivoAuth() {
    openRivoAccountHome();
  }

  /**
   * Push fresh favorite_products from Rivo API into the Account Widget Alpine store when the widget
   * has already mounted (otherwise the Favorites tab shows a cached list after add/remove).
   */
  function applyProductsToRivoFavoriteStore(products) {
    const alpine = window.RivoProfileAlpine;
    if (!alpine || typeof alpine.store !== "function") return;

    const nextProducts = Array.isArray(products) ? products.slice() : [];

    let favStore;
    try {
      favStore = alpine.store("rivo_favorite_products");
    } catch {
      return;
    }
    if (favStore == null) return;

    /** New array references help Alpine/Vue reactivity vs in-place splice. */
    if (Array.isArray(favStore)) {
      favStore.splice(0, favStore.length);
      for (let i = 0; i < nextProducts.length; i++) favStore.push(nextProducts[i]);
      return;
    }

    if (typeof favStore === "object") {
      if (Array.isArray(favStore.products)) {
        favStore.products = nextProducts;
        return;
      }
      if ("products" in favStore) {
        favStore.products = nextProducts;
        return;
      }
    }

    /** Some Rivo builds allow replacing the whole store (Alpine 3 two-arg); merge to preserve other keys. */
    try {
      const existing = alpine.store("rivo_favorite_products");
      if (existing && typeof existing === "object" && !Array.isArray(existing)) {
        alpine.store("rivo_favorite_products", Object.assign({}, existing, { products: nextProducts }));
      } else {
        alpine.store("rivo_favorite_products", { products: nextProducts });
      }
    } catch {
      /* ignore */
    }
  }

  /** Optional globals Rivo may expose (not documented on all versions). */
  function tryRivoGlobalFavoriteRefresh() {
    if (typeof window.rivoRefreshFavoriteProducts === "function") {
      try {
        window.rivoRefreshFavoriteProducts();
        return true;
      } catch {
        /* ignore */
      }
    }
    if (typeof window.rivoRefreshFavorites === "function") {
      try {
        window.rivoRefreshFavorites();
        return true;
      } catch {
        /* ignore */
      }
    }
    return false;
  }

  /**
   * Re-fetch server favorites and refresh the Rivo account widget list if it is open / already initialized.
   */
  function refreshRivoAccountFavoritesUI() {
    tryRivoGlobalFavoriteRefresh();

    favoriteProductsPromise()
      .then(function (data) {
        if (data == null || !shopifyCustomerLoggedIn()) return;
        const products = extractFavoriteProducts(data);
        applyProductsToRivoFavoriteStore(products);
      })
      .catch(function () {});
  }

  /** Staggered refetches: widget + API can lag; RivoProfileAlpine may mount after first tick. */
  const RIVO_FAVORITES_UI_REFRESH_DELAYS_MS = [0, 120, 350, 700, 1400, 2800, 5000];

  function scheduleRefreshRivoAccountFavoritesUI() {
    for (let i = 0; i < RIVO_FAVORITES_UI_REFRESH_DELAYS_MS.length; i++) {
      setTimeout(refreshRivoAccountFavoritesUI, RIVO_FAVORITES_UI_REFRESH_DELAYS_MS[i]);
    }
  }

  function getRivoAdd() {
    return window.RivoJS && typeof window.RivoJS.addFavoriteProduct === "function"
      ? window.RivoJS.addFavoriteProduct
      : null;
  }

  function getRivoRemove() {
    return window.RivoJS && typeof window.RivoJS.removeFavoriteProduct === "function"
      ? window.RivoJS.removeFavoriteProduct
      : null;
  }

  /**
   * 3) Click: mutate localStorage + buttons only. Request runs in background; do not apply response to UI.
   */
  function onClick(event) {
    const btn = event.target.closest(SELECTOR);
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    const productIdAttr = btn.getAttribute("data-product-id");
    if (!productIdAttr) return;

    const productIdNorm = normalizeProductId(productIdAttr);
    if (!productIdNorm) return;

    if (!shopifyCustomerLoggedIn()) {
      openRivoAuth();
      return;
    }

    const rivoAdd = getRivoAdd();
    const rivoRemove = getRivoRemove();

    const already = btn.getAttribute("aria-pressed") === "true";

    if (already) {
      if (!rivoRemove) {
        syncFromRivo();
        return;
      }

      const snapshot = readFavoriteIdsFromStorage();
      const next = new Set(snapshot);
      next.delete(productIdNorm);
      writeFavoriteIdsToStorage(next);
      applyFavoriteStateToButtons(next);
      reconcilePausedUntil = Date.now() + RECONCILE_PAUSE_MS;

      Promise.resolve(rivoRemove(Number(productIdNorm))).then(
        function (resp) {
          applyAddRemoveResponseToRivoStore(resp);
          scheduleRefreshRivoAccountFavoritesUI();
        },
        function () {
          writeFavoriteIdsToStorage(snapshot);
          applyFavoriteStateToButtons(snapshot);
        }
      );
      return;
    }

    if (!rivoAdd) {
      openRivoAuth();
      return;
    }

    const snapshot = readFavoriteIdsFromStorage();
    const next = new Set(snapshot);
    next.add(productIdNorm);
    writeFavoriteIdsToStorage(next);
    applyFavoriteStateToButtons(next);
    reconcilePausedUntil = Date.now() + RECONCILE_PAUSE_MS;

    Promise.resolve(rivoAdd(Number(productIdNorm))).then(
      function (resp) {
        applyAddRemoveResponseToRivoStore(resp);
        openRivoAccountHome();
        scheduleRefreshRivoAccountFavoritesUI();
      },
      function () {
        writeFavoriteIdsToStorage(snapshot);
        applyFavoriteStateToButtons(snapshot);
        openRivoAuth();
      }
    );
  }

  document.addEventListener("click", onClick, true);

  /**
   * Wishlist buttons are replaced by PDP gallery fetches (color/variant) and section morphs.
   * New nodes default to aria-pressed=false; re-apply localStorage (and let the next syncFromRivo
   * reconcile server) so hearts stay correct.
   */
  function scheduleHydrateUIFromStorage() {
    requestAnimationFrame(function () {
      hydrateUIFromStorage();
    });
  }

  document.addEventListener("bww:rivo-wishlist-hydrate", scheduleHydrateUIFromStorage);
  document.addEventListener("shopify:section:load", scheduleHydrateUIFromStorage);

  window.addEventListener("storage", function (event) {
    if (!event.key || event.key.indexOf(STORAGE_KEY_PREFIX) !== 0) return;
    scheduleHydrateUIFromStorage();
  });

  hydrateUIFromStorage();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleBackgroundSync);
  } else {
    scheduleBackgroundSync();
  }

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") {
      syncFromRivo();
    }
  });
})();
